import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getProductProcesses } from 'app/entities/product-process/product-process.reducer';
import { ProcessType } from 'app/shared/model/enumerations/process-type.model';
import { StepType } from 'app/shared/model/enumerations/step-type.model';
import { StepStatus } from 'app/shared/model/enumerations/step-status.model';
import { createEntity, getEntity, reset, updateEntity } from './product-process-history.reducer';

export const ProductProcessHistoryUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const productProcesses = useAppSelector(state => state.productProcess.entities);
  const productProcessHistoryEntity = useAppSelector(state => state.productProcessHistory.entity);
  const loading = useAppSelector(state => state.productProcessHistory.loading);
  const updating = useAppSelector(state => state.productProcessHistory.updating);
  const updateSuccess = useAppSelector(state => state.productProcessHistory.updateSuccess);
  const processTypeValues = Object.keys(ProcessType);
  const stepTypeValues = Object.keys(StepType);
  const stepStatusValues = Object.keys(StepStatus);

  const handleClose = () => {
    navigate('/product-process-history');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getProductProcesses({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.retryCount !== undefined && typeof values.retryCount !== 'number') {
      values.retryCount = Number(values.retryCount);
    }
    values.createdAt = convertDateTimeToServer(values.createdAt);

    const entity = {
      ...productProcessHistoryEntity,
      ...values,
      productProcess: productProcesses.find(it => it.id.toString() === values.productProcess?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdAt: displayDefaultDateTime(),
        }
      : {
          processType: 'MOVE_IN',
          stepType: 'CREATED',
          stepStatus: 'PENDING',
          ...productProcessHistoryEntity,
          createdAt: convertDateTimeFromServer(productProcessHistoryEntity.createdAt),
          productProcess: productProcessHistoryEntity?.productProcess?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2
            id="crawlerBackendApp.productProcessHistory.home.createOrEditLabel"
            data-cy="ProductProcessHistoryCreateUpdateHeading"
          >
            <Translate contentKey="crawlerBackendApp.productProcessHistory.home.createOrEditLabel">
              Create or edit a ProductProcessHistory
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="product-process-history-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.processType')}
                id="product-process-history-processType"
                name="processType"
                data-cy="processType"
                type="select"
              >
                {processTypeValues.map(processType => (
                  <option value={processType} key={processType}>
                    {translate(`crawlerBackendApp.ProcessType.${processType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.stepType')}
                id="product-process-history-stepType"
                name="stepType"
                data-cy="stepType"
                type="select"
              >
                {stepTypeValues.map(stepType => (
                  <option value={stepType} key={stepType}>
                    {translate(`crawlerBackendApp.StepType.${stepType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.stepStatus')}
                id="product-process-history-stepStatus"
                name="stepStatus"
                data-cy="stepStatus"
                type="select"
              >
                {stepStatusValues.map(stepStatus => (
                  <option value={stepStatus} key={stepStatus}>
                    {translate(`crawlerBackendApp.StepStatus.${stepStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.retryCount')}
                id="product-process-history-retryCount"
                name="retryCount"
                data-cy="retryCount"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.payloadIn')}
                id="product-process-history-payloadIn"
                name="payloadIn"
                data-cy="payloadIn"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.payloadOut')}
                id="product-process-history-payloadOut"
                name="payloadOut"
                data-cy="payloadOut"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.coolingOffEnd')}
                id="product-process-history-coolingOffEnd"
                name="coolingOffEnd"
                data-cy="coolingOffEnd"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.offerExpirationDate')}
                id="product-process-history-offerExpirationDate"
                name="offerExpirationDate"
                data-cy="offerExpirationDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.mandateSignedDate')}
                id="product-process-history-mandateSignedDate"
                name="mandateSignedDate"
                data-cy="mandateSignedDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.desiredDate')}
                id="product-process-history-desiredDate"
                name="desiredDate"
                data-cy="desiredDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.actualStartDate')}
                id="product-process-history-actualStartDate"
                name="actualStartDate"
                data-cy="actualStartDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.transactionId')}
                id="product-process-history-transactionId"
                name="transactionId"
                data-cy="transactionId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.waiveCoolingOffRight')}
                id="product-process-history-waiveCoolingOffRight"
                name="waiveCoolingOffRight"
                data-cy="waiveCoolingOffRight"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.depositRequired')}
                id="product-process-history-depositRequired"
                name="depositRequired"
                data-cy="depositRequired"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.deposit')}
                id="product-process-history-deposit"
                name="deposit"
                data-cy="deposit"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productProcessHistory.createdAt')}
                id="product-process-history-createdAt"
                name="createdAt"
                data-cy="createdAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                id="product-process-history-productProcess"
                name="productProcess"
                data-cy="productProcess"
                label={translate('crawlerBackendApp.productProcessHistory.productProcess')}
                type="select"
              >
                <option value="" key="0" />
                {productProcesses
                  ? productProcesses.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/product-process-history"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProductProcessHistoryUpdate;
